import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { LogOut, Plus, Trash2, Edit2, Check, X, QrCode, Menu, Copy, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import EditProfile from '../components/EditProfile';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getLinks, addLink, updateLink, deleteLink, getAnalytics, getClickAnalytics, getDeviceAnalytics, updateProfile, getMySubscribers, checkUsername, setUsername, createRazorpayOrder, verifyRazorpayPayment, createDodoCheckout } from '../services/api';
import { getCountryPricing, getCachedPricing, PricingConfig } from '../utils/pricing';
import { QRCodeSVG } from 'qrcode.react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import '../styles/global.css';
import '../styles/Dashboard.css';

interface LinkItem {
  _id: string;
  title: string;
  url: string;
  icon: string;
  isActive: boolean;
  clicks: number;
  isProtected: boolean;
  password: string;
  scheduleEnabled: boolean;
  scheduleStart: string | null;
  scheduleEnd: string | null;
  isLocked?: boolean;
  thumbnail?: string;
  isFeatured?: boolean;
}

const THEMES = [
  { id: 'noir',    name: '🌙 Noir',    bg: '#1C1208', accent: '#C0392B', preview: 'linear-gradient(135deg, #1C1208, #2a1e10)' },
  { id: 'ivory',   name: '☀️ Ivory',   bg: '#F5F0E8', accent: '#C0392B', preview: 'linear-gradient(135deg, #F5F0E8, #ede8e0)' },
  { id: 'ocean',   name: '🌊 Ocean',   bg: '#0A1628', accent: '#00BCD4', preview: 'linear-gradient(135deg, #0A1628, #1A3A5C)' },
  { id: 'forest',  name: '🌿 Forest',  bg: '#0D1F0D', accent: '#66BB6A', preview: 'linear-gradient(135deg, #0D1F0D, #1A3320)' },
  { id: 'sunset',  name: '🌅 Sunset',  bg: '#1A0A0A', accent: '#FF6B6B', preview: 'linear-gradient(135deg, #1A0A0A, #3D1515)' },
  { id: 'minimal', name: '🖤 Minimal', bg: '#FFFFFF', accent: '#111111', preview: 'linear-gradient(135deg, #FFFFFF, #f5f5f5)' },
  { id: 'custom',  name: '🎨 Custom',  bg: '#1C1208', accent: '#C0392B', preview: 'linear-gradient(135deg, #667eea, #764ba2)' },
];

const FONTS = [
  { id: 'Georgia, serif',               name: '📖 Classic' },
  { id: "'Courier New', monospace",     name: '💻 Mono' },
  { id: "'Helvetica Neue', sans-serif", name: '✏️ Modern' },
  { id: "'Times New Roman', serif",     name: '📰 Times' },
  { id: 'cursive',                      name: '✍️ Cursive' },
];

const SortableLink = ({ link, onEdit, onDelete, onToggle, onQR, editingId, editLink, setEditLink, handleEdit, setEditingId, onUpgrade, isPro, protectedLinkCount }: any) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: link._id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className={`link-card ${!link.isActive ? 'inactive' : ''}`}>
      {editingId === link._id ? (
        <div>
          <input type="text" value={editLink.title}
            onChange={(e) => setEditLink({ ...editLink, title: e.target.value })}
            className="edit-input" placeholder="Title"
          />
          <input type="url" value={editLink.url}
            onChange={(e) => setEditLink({ ...editLink, url: e.target.value })}
            className="edit-input" placeholder="URL"
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 6px' }}>
            <label className="toggle">
              <input type="checkbox" checked={editLink.isProtected}
                onChange={(e) => {
                  // Free users can only have 3 password protected links
                  if (!isPro && !editLink.isProtected && protectedLinkCount >= 3) {
                    onUpgrade();
                    return;
                  }
                  setEditLink({ ...editLink, isProtected: e.target.checked });
                }}
              />
              <span className="toggle-slider" />
            </label>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>
              🔒 Password protect
              {!isPro && <span style={{ fontSize: 11, color: 'var(--accent)', marginLeft: 6 }}>({protectedLinkCount}/3 free)</span>}
            </span>
          </div>
          {editLink.isProtected && (
            <input type="text" value={editLink.password}
              onChange={(e) => setEditLink({ ...editLink, password: e.target.value })}
              className="edit-input" placeholder="Set password for this link"
            />
          )}
          {/* --- Schedule Toggle (edit) --- */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 4px' }}>
            <label className="toggle">
              <input type="checkbox" checked={editLink.scheduleEnabled}
                onChange={(e) => setEditLink({ ...editLink, scheduleEnabled: e.target.checked })}
              />
              <span className="toggle-slider" />
            </label>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>🗓️ Schedule this link</span>
          </div>
          {editLink.scheduleEnabled && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '4px 0' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 3 }}>Start (optional)</label>
                <input type="datetime-local" value={editLink.scheduleStart}
                  onChange={(e) => setEditLink({ ...editLink, scheduleStart: e.target.value })}
                  className="edit-input" style={{ fontSize: 12 }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 3 }}>End (optional)</label>
                <input type="datetime-local" value={editLink.scheduleEnd}
                  onChange={(e) => setEditLink({ ...editLink, scheduleEnd: e.target.value })}
                  className="edit-input" style={{ fontSize: 12 }}
                />
              </div>
            </div>
          )}
          {/* Featured toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 4px' }}>
            <label className="toggle">
              <input type="checkbox" checked={editLink.isFeatured || false}
                onChange={(e) => setEditLink({ ...editLink, isFeatured: e.target.checked })} />
              <span className="toggle-slider" />
            </label>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>⭐ Feature this link</span>
          </div>
          {/* Thumbnail */}
          <div style={{ margin: '6px 0 10px' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>🖼️ Thumbnail (optional)</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {editLink.thumbnail && (
                <img src={editLink.thumbnail} alt="thumb" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
              )}
              <label style={{
                padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
                fontSize: 12, cursor: 'pointer', color: 'var(--muted)', background: 'var(--bg-alt)',
              }}>
                {editLink.thumbnail ? 'Change' : 'Upload'}
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => setEditLink({ ...editLink, thumbnail: reader.result as string });
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
              {editLink.thumbnail && (
                <button onClick={() => setEditLink({ ...editLink, thumbnail: '' })}
                  style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>
                  Remove
                </button>
              )}
            </div>
          </div>
          <div className="edit-actions">
            <button onClick={() => handleEdit(link._id)} className="btn-save"><Check size={15} /></button>
            <button onClick={() => setEditingId(null)} className="btn-discard"><X size={15} /></button>
          </div>
        </div>
      ) : link.isLocked ? (
        /* ── LOCKED LINK (free user, 3rd onward) ── */
        <div className="link-card-inner" style={{ opacity: 0.6, position: 'relative' }}>
          <div className="drag-handle" style={{ color: 'var(--muted)' }}>⠿</div>
          <div className="link-info">
            <div className="link-title" style={{ filter: 'blur(4px)', userSelect: 'none' }}>{link.title}</div>
            <div className="link-url" style={{ filter: 'blur(3px)', userSelect: 'none' }}>{link.url}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <div style={{
              background: 'var(--accent)', color: '#fff', fontSize: 11, fontWeight: 700,
              padding: '4px 10px', borderRadius: 20, cursor: 'pointer', whiteSpace: 'nowrap',
            }} onClick={onUpgrade}>
              🔒 Unlock Pro
            </div>
          </div>
        </div>
      ) : (
        <div className="link-card-inner" style={{ borderLeft: link.isFeatured ? '3px solid var(--accent)' : undefined }}>
          <div {...attributes} {...listeners} className="drag-handle">⠿</div>
          {link.thumbnail && (
            <img src={link.thumbnail} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover', marginRight: 8, flexShrink: 0 }} />
          )}
          <div className="link-info">
            <div className="link-title">
              {link.isFeatured   && <span style={{ marginRight: 4 }} title="Featured">⭐</span>}
              {link.isProtected  && <span style={{ marginRight: 4 }}>🔒</span>}
              {link.scheduleEnabled && <span style={{ marginRight: 4 }} title="Scheduled">🗓️</span>}
              {link.title}
            </div>
            <div className="link-url">{link.url}</div>
          </div>
          <div className="link-clicks">👆 {link.clicks || 0}</div>
          <div className="link-actions">
            <label className="toggle" title={link.isActive ? 'Hide' : 'Show'}>
              <input type="checkbox" checked={link.isActive} onChange={() => onToggle(link._id, link.isActive)} />
              <span className="toggle-slider" />
            </label>
            <button onClick={() => onQR(link)} className="link-btn" title="QR Code"><QrCode size={14} /></button>
            <button onClick={() => {
              onEdit(link._id);
              setEditLink({
                title: link.title, url: link.url,
                isProtected: link.isProtected || false, password: '',
                scheduleEnabled: link.scheduleEnabled || false,
                scheduleStart: link.scheduleStart ? link.scheduleStart.slice(0,16) : '',
                scheduleEnd: link.scheduleEnd ? link.scheduleEnd.slice(0,16) : '',
                isFeatured: link.isFeatured || false,
                thumbnail: link.thumbnail || '',
              });
            }} className="link-btn"><Edit2 size={14} /></button>
            <button onClick={() => onDelete(link._id)} className="link-btn delete"><Trash2 size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

const Avatar = ({ user, size = 40, className = '' }: { user: any; size?: number; className?: string }) => {
  if (user?.avatar) return <img src={user.avatar} alt={user.name} className={`sidebar-avatar-img ${className}`} style={{ width: size, height: size }} />;
  return <div className={`sidebar-avatar ${className}`} style={{ width: size, height: size }}>{user?.name?.charAt(0).toUpperCase()}</div>;
};

const Dashboard = () => {
  const { user, logoutUser, updateUser } = useAuth();
  const [links, setLinks]             = useState<LinkItem[]>([]);
  const [loading, setLoading]         = useState(true);
  const [adding, setAdding]           = useState(false);
  const [editingId, setEditingId]     = useState<string | null>(null);
  const [newLink, setNewLink]         = useState({ title: '', url: '', isProtected: false, password: '', scheduleEnabled: false, scheduleStart: '', scheduleEnd: '', isFeatured: false, thumbnail: '' });
  const [editLink, setEditLink]       = useState({ title: '', url: '', isProtected: false, password: '', scheduleEnabled: false, scheduleStart: '', scheduleEnd: '', isFeatured: false, thumbnail: '' });
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [views, setViews]             = useState(0);
  const [showQR, setShowQR]           = useState<{ title: string; url: string } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab]     = useState('links');
  const [subscribers, setSubscribers] = useState<{ email: string; subscribedAt: string }[]>([]);
  const [subLoading, setSubLoading]   = useState(false);
  const [menuOpen, setMenuOpen]       = useState(false);
  const [clickData, setClickData]     = useState<any>({ chartData: [], linkTitles: [] });
  const [deviceData, setDeviceData]   = useState<any>(null);
  const [showThemePicker, setShowThemePicker] = useState(false);

  const [selectedTheme, setSelectedTheme]     = useState(user?.theme || 'noir');
  const [savingTheme, setSavingTheme]         = useState(false);
  const [customTheme, setCustomTheme] = useState({ bg: '#1C1208', text: '#F5F0E8', accent: '#C0392B', font: 'Georgia, serif' });
  const [urlCopied, setUrlCopied]     = useState(false);
  const [usernameSetup, setUsernameSetup]   = useState(false);
  const [newUsername, setNewUsername]       = useState('');
  const [savingUsername, setSavingUsername] = useState(false);
  const [usernameAvail, setUsernameAvail]   = useState<null | boolean>(null);
  const [availMsg, setAvailMsg]             = useState('');
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradePlan, setUpgradePlan]           = useState<'monthly' | 'yearly'>('monthly');
  const [upgradeLoading, setUpgradeLoading]     = useState(false);
  const [pricing, setPricing]                   = useState<PricingConfig>(getCachedPricing());

  useEffect(() => {
    fetchLinks(); fetchAnalytics(); fetchClickAnalytics(); fetchDeviceAnalytics(); fetchSubscribers();
    getCountryPricing().then(setPricing);
  }, []);
  useEffect(() => { if (user?.theme) setSelectedTheme(user.theme); }, [user]);

  // Agar username null/empty hai toh modal dikhao
  useEffect(() => {
    if (user) {
      setUsernameSetup(!user.username?.trim());
    }
  }, [user]);

  // Debounced availability check
  useEffect(() => {
    if (newUsername.length < 3) { setUsernameAvail(null); setAvailMsg(''); return; }
    const timer = setTimeout(async () => {
      setCheckingUsername(true);
      try {
        const res = await checkUsername(newUsername);
        setUsernameAvail(res.data.available);
        setAvailMsg(res.data.message);
      } catch { setUsernameAvail(null); }
      finally { setCheckingUsername(false); }
    }, 500);
    return () => clearTimeout(timer);
  }, [newUsername]);

  const fetchAnalytics = async () => {
    try { const res = await getAnalytics(); setViews(res.data.views); } catch {}
  };

  const fetchSubscribers = async () => {
    try { setSubLoading(true); const res = await getMySubscribers(); setSubscribers(res.data); }
    catch {} finally { setSubLoading(false); }
  };

  const fetchClickAnalytics = async () => {
    try {
      const res = await getClickAnalytics();
      const raw = res.data;
      const days: string[] = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i);
        days.push(d.toISOString().split('T')[0]);
      }
      const linkMap: { [key: string]: { [date: string]: number } } = {};
      raw.forEach((item: any) => {
        const title = item.linkTitle || item.linkId;
        if (!linkMap[title]) linkMap[title] = {};
        linkMap[title][item.date] = item.count;
      });
      const chartData = days.map(day => {
        const entry: any = { date: day.slice(5) };
        Object.keys(linkMap).forEach(title => { entry[title] = linkMap[title][day] || 0; });
        return entry;
      });
      setClickData({ chartData, linkTitles: Object.keys(linkMap) });
    } catch {}
  };

  const fetchDeviceAnalytics = async () => {
    try {
      const res = await getDeviceAnalytics();
      setDeviceData(res.data);
    } catch {}
  };

  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      if (pricing.gateway === 'razorpay') {
        // Razorpay for India
        const res = await createRazorpayOrder(upgradePlan);
        const { subscriptionId, keyId } = res.data;
        const rzp = new (window as any).Razorpay({
          key: keyId,
          subscription_id: subscriptionId,
          name: 'mylinx',
          description: `PRO ${upgradePlan} plan`,
          image: 'https://www.mylinx.in/favicon-32x32.png',
          handler: async (response: any) => {
            try {
              await verifyRazorpayPayment({ ...response, plan: upgradePlan });
              toast.success('PRO unlocked! 🎉 Please refresh.');
              setShowUpgradeModal(false);
              updateUser({ isPremium: true });
            } catch { toast.error('Verification failed!'); }
          },
          prefill: { email: user?.email, name: user?.name },
          theme: { color: '#C0392B' },
        });
        rzp.open();
      } else {
        // Dodo Payments for international
        const res = await createDodoCheckout(upgradePlan);
        window.location.href = res.data.checkoutUrl;
      }
    } catch (err) {
      toast.error('Something went wrong!');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleSaveTheme = async () => {
    setSavingTheme(true);
    try {
      const payload = selectedTheme === 'custom'
        ? { theme: 'custom', customTheme }
        : { theme: selectedTheme, customTheme: { bg: '', text: '', accent: '', font: '' } };
      await updateProfile(payload);
      updateUser({ theme: selectedTheme, customTheme: payload.customTheme });
      toast.success('Theme saved!');
      setShowThemePicker(false);
    } catch { toast.error('Theme save nahi hua'); }
    finally { setSavingTheme(false); }
  };

  const handleSaveUsername = async () => {
    if (!newUsername || newUsername.length < 3) return toast.error('Min 3 characters!');
    if (!usernameAvail) return toast.error('Choose an available username first!');
    setSavingUsername(true);
    try {
      const res = await setUsername(newUsername);
      updateUser({ username: res.data.username });
      toast.success('Username set! 🎉');
      setUsernameSetup(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Username already taken!');
    } finally { setSavingUsername(false); }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = links.findIndex(l => l._id === active.id);
      const newIndex = links.findIndex(l => l._id === over?.id);
      const newLinks = arrayMove(links, oldIndex, newIndex);
      setLinks(newLinks);
      try { await Promise.all(newLinks.map((link, index) => updateLink(link._id, { order: index }))); }
      catch { toast.error('Order save nahi hua'); }
    }
  };

  const fetchLinks = async () => {
    try {
      const res = await getLinks();
      // New API returns { links, isPro } — old API returns array directly
      if (Array.isArray(res.data)) {
        setLinks(res.data);
      } else {
        setLinks(res.data.links);
      }
    }
    catch { toast.error('Links load nahi hue'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!newLink.title || !newLink.url) return toast.error('Title aur URL dono bharo!');
    try {
      const res = await addLink(newLink);
      setLinks([...links, res.data]);
      setNewLink({ title: '', url: '', isProtected: false, password: '', scheduleEnabled: false, scheduleStart: '', scheduleEnd: '', isFeatured: false, thumbnail: '' });
      setAdding(false);
      toast.success('Link added!');
    } catch { toast.error('Error adding link'); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteLink(id); setLinks(links.filter(l => l._id !== id)); toast.success('Link deleted!'); }
    catch { toast.error('Error deleting link'); }
  };

  const handleEdit = async (id: string) => {
    try {
      const res = await updateLink(id, editLink);
      setLinks(links.map(l => l._id === id ? res.data : l));
      setEditingId(null);
      toast.success('Link updated!');
    } catch { toast.error('Update failed'); }
  };

  const handleToggle = async (id: string, currentStatus: boolean) => {
    try {
      const res = await updateLink(id, { isActive: !currentStatus });
      setLinks(links.map(l => l._id === id ? res.data : l));
      toast.success(!currentStatus ? 'Link visible!' : 'Link hidden!');
    } catch { toast.error('Toggle failed'); }
  };


  // Password modal state
  const [pwdCurrent, setPwdCurrent] = React.useState("");
  const [pwdNew, setPwdNew]         = React.useState("");
  const [pwdConfirm, setPwdConfirm] = React.useState("");
  const [pwdLoading, setPwdLoading] = React.useState(false);

  const handleChangePassword = async () => {
    if (!pwdNew || !pwdConfirm) return toast.error("Sab fields bharo!");
    if (pwdNew !== pwdConfirm) return toast.error("Passwords match nahi kar rahe!");
    if (pwdNew.length < 6) return toast.error("Min 6 characters!");
    if (user?.passwordSet && !pwdCurrent) return toast.error("Current password required!");
    setPwdLoading(true);
    try {
      const { changePassword } = await import("../services/api");
      await changePassword({ currentPassword: pwdCurrent, newPassword: pwdNew });
      toast.success("Password updated! 🎉");
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
      setShowPasswordModal(false);
      if (!user?.passwordSet) updateUser({ passwordSet: true });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed!");
    } finally { setPwdLoading(false); }
  };
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(`https://${profileUrl}`);
    setUrlCopied(true);
    toast.success('URL copied!');
    setTimeout(() => setUrlCopied(false), 2000);
  };

  const totalClicks = links.reduce((sum, l) => sum + (l.clicks || 0), 0);
  const activeLinks = links.filter(l => l.isActive).length;
  const clickRate   = views > 0 ? ((totalClicks / views) * 100).toFixed(1) : '0.0';
  const profileUrl  = `mylinx.in/${user?.username || ''}`;

  const downloadCSV = () => {
    const csv = ['Email,Subscribed At', ...subscribers.map(s =>
      `${s.email},${new Date(s.subscribedAt).toLocaleDateString()}`
    )].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'mylinx-subscribers.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const PreviewContent = () => {
    const activeLinks = links.filter(l => l.isActive);
    const visibleLinks = activeLinks.filter(l => !l.isLocked);
    const lockedLinks  = activeLinks.filter(l => l.isLocked);
    return (
      <div className="preview-phone">
        <div className="preview-avatar-wrap">
          {user?.avatar ? <img src={user.avatar} alt={user.name} className="preview-avatar-img" />
            : <div className="preview-avatar">{user?.name?.charAt(0).toUpperCase()}</div>}
        </div>
        <div className="preview-name">{user?.name}</div>
        <div className="preview-username">@{user?.username}</div>
        {user?.bio && <div className="preview-bio">{user.bio}</div>}
        <div style={{ marginTop: 16 }}>
          {visibleLinks.map(link => (
            <a key={link._id} href={link.url} target="_blank" rel="noopener noreferrer" className="preview-link">
              {link.title}
            </a>
          ))}
          {lockedLinks.length > 0 && (
            <div style={{
              border: '1.5px dashed var(--border)',
              borderRadius: 10, padding: '14px 12px',
              textAlign: 'center', marginTop: 8,
              background: 'rgba(192,57,43,0.06)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                🔒 {lockedLinks.length} link{lockedLinks.length > 1 ? 's' : ''} hidden
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
                Upgrade to PRO to show all links
              </div>
              <div
                onClick={() => setShowUpgradeModal(true)}
                style={{
                  background: 'var(--accent)', color: '#fff',
                  padding: '6px 14px', borderRadius: 20,
                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  display: 'inline-block',
                }}
              >
                Upgrade to PRO →
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const SidebarNav = () => (
    <>
      <button className={`sidebar-btn ${activeTab === 'links' ? 'active' : ''}`} onClick={() => { setActiveTab('links'); setMenuOpen(false); }}><span className="btn-icon">🔗</span> My Links</button>
      <button className={`sidebar-btn ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => { setActiveTab('analytics'); setMenuOpen(false); }}><span className="btn-icon">📊</span> Analytics</button>
      <button className={`sidebar-btn ${activeTab === 'subscribers' ? 'active' : ''}`} onClick={() => { setActiveTab('subscribers'); setMenuOpen(false); }}><span className="btn-icon">📧</span> Subscribers</button>
      <button className="sidebar-btn" onClick={() => { setShowPreview(!showPreview); setMenuOpen(false); }}><span className="btn-icon">👁</span> Preview</button>
      <button className="sidebar-btn" onClick={() => { setShowQR({ title: profileUrl, url: `https://${profileUrl}` }); setMenuOpen(false); }}><span className="btn-icon">◻</span> QR Code</button>
      <button className="sidebar-btn" onClick={() => { setShowThemePicker(true); setMenuOpen(false); }}><span className="btn-icon">🎨</span> Theme</button>
      <button className="sidebar-btn" onClick={() => { setShowEditProfile(true); setMenuOpen(false); }}><span className="btn-icon">✎</span> Edit Profile</button>
      <button className="sidebar-btn" onClick={() => { setShowPasswordModal(true); setMenuOpen(false); }}><span className="btn-icon">🔑</span> Change Password</button>
    </>
  );

  return (
    <div className="dashboard-page">

      {/* USERNAME SETUP MODAL */}
      {usernameSetup && (
        <div className="username-setup-overlay">
          <div className="username-setup-modal">
            <div className="username-setup-emoji">🔗</div>
            <div className="username-setup-title">Claim your <em>link</em></div>
            <div className="username-setup-sub">
              Choose your unique username — this will be your permanent mylinx URL.
            </div>
            <div className="username-setup-input-wrap">
              <span className="username-setup-prefix">mylinx.in/</span>
              <input
                className="username-setup-input"
                type="text"
                placeholder="yourname"
                value={newUsername}
                autoFocus
                onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-zA-Z0-9_]/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveUsername(); }}
              />
            </div>
            {newUsername.length >= 3 && (
              <div style={{
                textAlign: 'left', marginBottom: 12,
                fontFamily: 'var(--font-mono)', fontSize: 12,
                color: checkingUsername ? 'var(--muted)' : usernameAvail ? '#22c55e' : '#ef4444'
              }}>
                {checkingUsername ? '⏳ Checking...' : usernameAvail ? '✓ Available!' : `✕ ${availMsg}`}
              </div>
            )}
            <div className="username-setup-hint">Only letters, numbers and underscore.</div>
            <button
              className="username-setup-btn"
              onClick={handleSaveUsername}
              disabled={savingUsername || !usernameAvail || checkingUsername}
            >
              {savingUsername ? 'Saving...' : 'Claim my link →'}
            </button>
          </div>
        </div>
      )}

      {/* MOBILE NAVBAR */}
      <div className="mobile-navbar">
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div className="mobile-nav-logo">my<em>linx</em></div>
        </Link>
        <button className="mobile-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* MOBILE DRAWER */}
      {menuOpen && (
        <div className="mobile-drawer-overlay" onClick={() => setMenuOpen(false)}>
          <div className="mobile-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-drawer-header">
              <Link to="/" style={{ textDecoration: 'none' }}>
                <div className="mobile-drawer-title">my<em>linx</em></div>
              </Link>
              <button className="mobile-drawer-close" onClick={() => setMenuOpen(false)}><X size={16} /></button>
            </div>
            <div className="mobile-drawer-profile">
              <Avatar user={user} size={36} />
              <div>
                <div className="sidebar-name">{user?.name}</div>
                <div className="sidebar-username">@{user?.username}</div>
              </div>
            </div>
            <div className="mobile-drawer-nav"><SidebarNav /></div>
            <button className="sidebar-logout" onClick={logoutUser}><LogOut size={14} /> Logout</button>
          </div>
        </div>
      )}

      {/* DESKTOP SIDEBAR */}
      <aside className="sidebar">
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div className="sidebar-logo">my<em>linx</em></div>
        </Link>
        <div className="sidebar-profile">
          <Avatar user={user} size={40} />
          <div style={{ minWidth: 0 }}>
            <div className="sidebar-name">{user?.name}</div>
            <div className="sidebar-username">@{user?.username}</div>
          </div>
        </div>
        <nav className="sidebar-nav"><SidebarNav /></nav>
        <div className="sidebar-divider" />
        <button className="sidebar-logout" onClick={logoutUser}><LogOut size={14} /> Logout</button>
      </aside>

      {/* MAIN */}
      <main className="dash-main" style={{ marginRight: showPreview ? 320 : 0 }}>

        <div className="dash-topbar">
          <div>
            <div className="dash-page-title">
              {activeTab === 'links' && <>My <em>Links</em></>}
              {activeTab === 'analytics' && <><em>Analytics</em></>}
              {activeTab === 'subscribers' && <><em>Subscribers</em></>}
            </div>
            <div className="dash-page-sub">
              {profileUrl}
              <button className="copy-url-btn" onClick={handleCopyUrl} title="Copy URL">
                {urlCopied ? '✓' : <Copy size={12} />}
              </button>
              <a href={`https://${profileUrl}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
                <ExternalLink size={12} />
              </a>
            </div>
          </div>
          {activeTab === 'links' && (
            <button onClick={() => setAdding(true)} className="add-link-btn">
              <Plus size={15} /> Add Link
            </button>
          )}
        </div>

        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-card-icon">👁</div>
            <div className="stat-card-num accent">{views}</div>
            <div className="stat-card-label">Profile Views</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon">👆</div>
            <div className="stat-card-num">{totalClicks}</div>
            <div className="stat-card-label">Total Clicks</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon">🔗</div>
            <div className="stat-card-num">{activeLinks}</div>
            <div className="stat-card-label">Active Links</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon">📈</div>
            <div className="stat-card-num">{clickRate}%</div>
            <div className="stat-card-label">Click Rate</div>
          </div>
        </div>

        {activeTab === 'links' && (
          <>
            {adding && (
              <div className="add-form-card">
                <div className="add-form-title">Add New Link</div>
                <input type="text" placeholder="Title (e.g. My Instagram)"
                  value={newLink.title}
                  onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                  className="add-form-input"
                />
                <input type="url" placeholder="URL (e.g. https://instagram.com/username)"
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  className="add-form-input"
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 4px' }}>
                  <label className="toggle">
                    <input type="checkbox" checked={newLink.isProtected}
                      onChange={(e) => setNewLink({ ...newLink, isProtected: e.target.checked })}
                    />
                    <span className="toggle-slider" />
                  </label>
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>🔒 Password protect</span>
                </div>
                {newLink.isProtected && (
                  <input type="text" placeholder="Set password for this link"
                    value={newLink.password}
                    onChange={(e) => setNewLink({ ...newLink, password: e.target.value })}
                    className="add-form-input"
                  />
                )}
                {/* --- Schedule Toggle --- */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 4px' }}>
                  <label className="toggle">
                    <input type="checkbox" checked={newLink.scheduleEnabled}
                      onChange={(e) => setNewLink({ ...newLink, scheduleEnabled: e.target.checked })}
                    />
                    <span className="toggle-slider" />
                  </label>
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>🗓️ Schedule this link</span>
                </div>
                {newLink.scheduleEnabled && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '4px 0' }}>
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 3 }}>Start (optional)</label>
                      <input type="datetime-local" value={newLink.scheduleStart}
                        onChange={(e) => setNewLink({ ...newLink, scheduleStart: e.target.value })}
                        className="add-form-input" style={{ fontSize: 12 }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 3 }}>End (optional)</label>
                      <input type="datetime-local" value={newLink.scheduleEnd}
                        onChange={(e) => setNewLink({ ...newLink, scheduleEnd: e.target.value })}
                        className="add-form-input" style={{ fontSize: 12 }}
                      />
                    </div>
                  </div>
                )}
                <div className="add-form-actions">
                  <button onClick={handleAdd} className="btn-confirm">Add Link</button>
                  <button onClick={() => { setAdding(false); setNewLink({ title: '', url: '', isProtected: false, password: '', scheduleEnabled: false, scheduleStart: '', scheduleEnd: '', isFeatured: false, thumbnail: '' }); }} className="btn-cancel">Cancel</button>
                </div>
              </div>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={links.map(l => l._id)} strategy={verticalListSortingStrategy}>
                {loading ? (
                  <p className="loading-text">Loading...</p>
                ) : links.length === 0 ? (
                  <div className="empty-state">
                    <p>No links yet</p>
                    <span>Add your first link above!</span>
                  </div>
                ) : (
                  links.map((link) => (
                    <SortableLink key={link._id} link={link}
                      onEdit={setEditingId} onDelete={handleDelete}
                      onToggle={handleToggle} onQR={setShowQR}
                      editingId={editingId} editLink={editLink}
                      setEditLink={setEditLink} handleEdit={handleEdit}
                      setEditingId={setEditingId}
                      onUpgrade={() => setShowUpgradeModal(true)}
                      isPro={(user as any)?.isPremium || (user as any)?.isTrial}
                      protectedLinkCount={links.filter(l => l.isProtected).length}
                    />
                  ))
                )}
              </SortableContext>
            </DndContext>
          </>
        )}

        {activeTab === 'analytics' && (
          <div>
            <div className="analytics-chart-card">
              <div className="analytics-chart-title">
                <span>Clicks — Last 7 Days</span>
                {clickData.linkTitles?.length > 0 && (
                  <span className="analytics-chart-badge">{clickData.linkTitles.length} links tracked</span>
                )}
              </div>
              {clickData.linkTitles?.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={clickData.chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--muted)', fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--muted)', fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12, fontFamily: 'var(--font-mono)' }} />
                    {clickData.linkTitles.map((title: string, i: number) => (
                      <Line key={title} type="monotone" dataKey={title}
                        stroke={i === 0 ? 'var(--accent)' : `hsl(${i * 60 + 180}, 65%, 50%)`}
                        strokeWidth={2.5} dot={false} activeDot={{ r: 4, strokeWidth: 0 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="analytics-empty">
                  No click data yet — share your profile to start tracking!
                </div>
              )}
            </div>

            <div className="section-header" style={{ marginBottom: 14 }}>
              <div className="section-title">Link Performance</div>
            </div>
            {links.length === 0 ? (
              <div className="empty-state"><p>No links to analyze yet</p></div>
            ) : (
              [...links].sort((a, b) => (b.clicks || 0) - (a.clicks || 0)).map((link) => (
                <div key={link._id} className="perf-card">
                  <div className="perf-card-inner">
                    <div className="perf-info">
                      <div className="perf-title">{link.isProtected && '🔒 '}{link.title}</div>
                      <div className="perf-url">{link.url}</div>
                      <div className="perf-bar-wrap">
                        <div className="perf-bar" style={{ width: `${Math.min(((link.clicks || 0) / Math.max(totalClicks, 1)) * 100, 100)}%` }} />
                      </div>
                    </div>
                    <div className="perf-right">
                      <div className="perf-clicks">{link.clicks || 0}</div>
                      <div className="perf-clicks-label">clicks</div>
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* ── Premium Analytics ─────────────────────────────── */}
            {(() => {
              const isPremium = (user as any)?.isPremium || (user as any)?.isTrial || false;
              const isTrial = (user as any)?.isTrial || false;
              const trialDaysLeft = (user as any)?.trialDaysLeft || 0;

              // Reusable compact list inside one card
              const CompactList = ({ items, maxKey }: { items: any[], maxKey: number }) => {
                const [expanded, setExpanded] = React.useState(false);
                const filtered = items.filter((i: any) => i._id && i._id !== 'Unknown' && i._id !== 'Local');
                const shown = expanded ? filtered : filtered.slice(0, 5);
                if (filtered.length === 0) return <div className="analytics-empty" style={{ padding: '14px 0' }}>No data yet</div>;
                return (
                  <div>
                    {shown.map((item: any, idx: number) => (
                      <div key={item._id}>
                        {idx > 0 && <div style={{ borderTop: '1px solid var(--border)', margin: '0' }} />}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 5 }}>{item._id}</div>
                            <div style={{ height: 4, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: 4, background: 'var(--accent)', width: `${Math.min((item.count / (maxKey || 1)) * 100, 100)}%`, transition: 'width 0.4s ease' }} />
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', minWidth: 36 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent)' }}>{item.count}</div>
                            <div style={{ fontSize: 10, color: 'var(--muted)' }}>clicks</div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filtered.length > 5 && (
                      <button onClick={() => setExpanded(!expanded)} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 12, cursor: 'pointer', padding: '6px 0 2px', fontFamily: 'var(--font-mono)' }}>
                        {expanded ? '▲ Show less' : `▼ See all ${filtered.length}`}
                      </button>
                    )}
                  </div>
                );
              };

              const PremiumOverlay = () => (
                <div style={{ position: 'absolute', inset: 0, borderRadius: 14, backdropFilter: 'blur(6px)', background: 'rgba(var(--bg-rgb, 28,18,8), 0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, gap: 8 }}>
                  <div style={{ fontSize: 28 }}>🔒</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Premium Feature</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', maxWidth: 180 }}>Upgrade to unlock location & device analytics</div>
                  <button onClick={() => setShowUpgradeModal(true)} style={{ marginTop: 6, padding: '7px 18px', borderRadius: 20, border: '1.5px solid var(--accent)', background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>
                    Upgrade to PRO →
                  </button>
                </div>
              );

              return (
                <div style={{ marginTop: 28, position: 'relative' }}>
                  {/* Badge row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div className="section-title">Deep Analytics</div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'var(--accent)', color: '#fff', letterSpacing: 1, fontFamily: 'var(--font-mono)' }}>PRO</span>
                    <span className="analytics-chart-badge">Last 30 days</span>
                  </div>

                  {/* Trial Banner */}
                  {isTrial && (
                    <div style={{ background: 'linear-gradient(135deg, rgba(200,75,47,0.1), rgba(200,75,47,0.05))', border: '1px solid var(--accent-border)', borderRadius: 12, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>⏳</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>Free Trial Active</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--font-mono)' }}>{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left — upgrade to keep access</div>
                        </div>
                      </div>
                      <button disabled style={{ padding: '6px 14px', borderRadius: 20, border: '1.5px solid var(--muted)', background: 'transparent', color: 'var(--muted)', fontSize: 11, fontWeight: 600, cursor: 'not-allowed', fontFamily: 'var(--font-mono)', opacity: 0.6 }}>
                        Coming Soon
                      </button>
                    </div>
                  )}

                  {/* Blur wrapper when not premium */}
                  <div style={{ position: 'relative', filter: isPremium ? 'none' : 'blur(3px)', pointerEvents: isPremium ? 'auto' : 'none', userSelect: isPremium ? 'auto' : 'none' }}>

                    {/* Row 1 — Countries + Cities */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      {/* Countries */}
                      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10, fontFamily: 'var(--font-mono)', letterSpacing: 0.5 }}>📍 COUNTRIES</div>
                        {deviceData ? <CompactList items={deviceData.countries} maxKey={deviceData.countries[0]?.count || 1} /> : <div className="analytics-empty">Loading...</div>}
                      </div>
                      {/* Cities */}
                      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10, fontFamily: 'var(--font-mono)', letterSpacing: 0.5 }}>🏙️ CITIES</div>
                        {deviceData ? <CompactList items={deviceData.cities} maxKey={deviceData.cities[0]?.count || 1} /> : <div className="analytics-empty">Loading...</div>}
                      </div>
                    </div>

                    {/* Row 2 — Device + OS */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {/* Device */}
                      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10, fontFamily: 'var(--font-mono)', letterSpacing: 0.5 }}>📱 DEVICES</div>
                        {deviceData ? <CompactList items={deviceData.devices} maxKey={deviceData.devices[0]?.count || 1} /> : <div className="analytics-empty">Loading...</div>}
                      </div>
                      {/* OS */}
                      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10, fontFamily: 'var(--font-mono)', letterSpacing: 0.5 }}>💻 OS</div>
                        {deviceData ? <CompactList items={deviceData.os} maxKey={deviceData.os[0]?.count || 1} /> : <div className="analytics-empty">Loading...</div>}
                      </div>
                    </div>

                    {/* Row 3 — Browsers + Referrers */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10, fontFamily: 'var(--font-mono)', letterSpacing: 0.5 }}>🌐 BROWSERS</div>
                        {deviceData ? <CompactList items={deviceData.browsers} maxKey={deviceData.browsers[0]?.count || 1} /> : <div className="analytics-empty">Loading...</div>}
                      </div>
                      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10, fontFamily: 'var(--font-mono)', letterSpacing: 0.5 }}>🔗 REFERRERS</div>
                        {deviceData?.referrers ? <CompactList items={deviceData.referrers} maxKey={deviceData.referrers[0]?.count || 1} /> : <div className="analytics-empty">Loading...</div>}
                      </div>
                    </div>
                  </div>

                  {/* Lock overlay */}
                  {!isPremium && <PremiumOverlay />}
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === 'subscribers' && (
          <div>
            <div className="subs-header-row">
              <div className="subs-count-badge">
                <span className="subs-count-num">{subscribers.length}</span>
                <span className="subs-count-label">total subscribers</span>
              </div>
              {subscribers.length > 0 && (
                <button className="csv-btn" onClick={downloadCSV}>⬇ Export CSV</button>
              )}
            </div>
            {subLoading ? (
              <p className="loading-text">Loading subscribers...</p>
            ) : subscribers.length === 0 ? (
              <div className="empty-state">
                <p>No subscribers yet</p>
                <span>Share your profile — visitors can subscribe from there!</span>
              </div>
            ) : (
              subscribers.map((sub, i) => (
                <div key={i} className="sub-card">
                  <div>
                    <div className="sub-email">📧 {sub.email}</div>
                    <div className="sub-date">
                      Subscribed {new Date(sub.subscribedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                  <div className="sub-index">#{i + 1}</div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {showPreview && (
        <>
          <div className="preview-modal-overlay" onClick={() => setShowPreview(false)}>
            <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
              <div className="preview-title">Preview <button onClick={() => setShowPreview(false)} className="preview-close"><X size={14} /></button></div>
              <PreviewContent />
            </div>
          </div>
          <div className="preview-panel">
            <div className="preview-title">Preview <button onClick={() => setShowPreview(false)} className="preview-close"><X size={14} /></button></div>
            <PreviewContent />
          </div>
        </>
      )}

      {showThemePicker && (
        <div className="qr-modal-overlay" onClick={() => setShowThemePicker(false)}>
          <div className="theme-modal" onClick={(e) => e.stopPropagation()}>
            <div className="theme-modal-header">
              <div className="qr-modal-title" style={{ marginBottom: 0 }}>🎨 Choose Theme</div>
              <button onClick={() => setShowThemePicker(false)} className="preview-close"><X size={14} /></button>
            </div>
            <div className="theme-modal-sub">Changes how your public profile looks</div>
            {!((user as any)?.isPremium || (user as any)?.isTrial) && (
              <div style={{ background: 'rgba(192,57,43,0.1)', border: '1px solid var(--accent)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>
                🔒 Themes & custom colors are PRO features.{' '}
                <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { setShowThemePicker(false); setShowUpgradeModal(true); }}>
                  Upgrade to PRO →
                </span>
              </div>
            )}
            <div className="theme-grid">
              {THEMES.map((theme) => {
                const isProTheme = theme.id !== 'noir'; // noir is free, rest paid
                const isPro = (user as any)?.isPremium || (user as any)?.isTrial;
                const locked = isProTheme && !isPro;
                return (
                <div key={theme.id}
                  className={`theme-card ${selectedTheme === theme.id ? 'selected' : ''} ${locked ? 'theme-locked' : ''}`}
                  onClick={() => locked ? setShowUpgradeModal(true) : setSelectedTheme(theme.id)}
                  style={{ position: 'relative', opacity: locked ? 0.7 : 1 }}>
                  <div className="theme-preview" style={{ background: theme.preview }}>
                    <div className="theme-preview-dot" style={{ background: theme.accent }} />
                    <div className="theme-preview-lines">
                      <div className="theme-preview-line" style={{ background: theme.accent, opacity: 0.9 }} />
                      <div className="theme-preview-line" style={{ background: 'rgba(255,255,255,0.3)' }} />
                      <div className="theme-preview-line" style={{ background: 'rgba(255,255,255,0.2)', width: '60%' }} />
                    </div>
                    {locked && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🔒</div>}
                  </div>
                  <div className="theme-name">{theme.name}</div>
                  {selectedTheme === theme.id && !locked && <div className="theme-check">✓</div>}
                </div>
                );
              })}
            </div>
            {selectedTheme === 'custom' && ((user as any)?.isPremium || (user as any)?.isTrial) && (
              <div className="custom-theme-builder">
                <div className="custom-theme-title">Customize Your Theme</div>
                {[
                  { label: 'Background', key: 'bg' },
                  { label: 'Text Color', key: 'text' },
                  { label: 'Accent Color', key: 'accent' },
                ].map(({ label, key }) => (
                  <div key={key} className="custom-theme-row">
                    <label className="custom-theme-label">{label}</label>
                    <div className="custom-theme-input-wrap">
                      <input type="color" value={(customTheme as any)[key]}
                        onChange={(e) => setCustomTheme({ ...customTheme, [key]: e.target.value })}
                        className="color-picker"
                      />
                      <span className="color-value">{(customTheme as any)[key]}</span>
                    </div>
                  </div>
                ))}
                <div className="custom-theme-row">
                  <label className="custom-theme-label">Font</label>
                  <select className="font-select" value={customTheme.font}
                    onChange={(e) => setCustomTheme({ ...customTheme, font: e.target.value })}>
                    {FONTS.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="custom-preview" style={{ background: customTheme.bg, color: customTheme.text, fontFamily: customTheme.font }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Your Name</div>
                  <div style={{ fontSize: 11, color: customTheme.accent, marginBottom: 10 }}>@username</div>
                  <div style={{ background: 'rgba(255,255,255,0.1)', border: `1px solid ${customTheme.accent}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, textAlign: 'center' }}>
                    My Link
                  </div>
                </div>
              </div>
            )}
            <div className="theme-modal-actions">
              <button onClick={() => setShowThemePicker(false)} className="btn-cancel">Cancel</button>
              <button onClick={handleSaveTheme} className="btn-confirm" disabled={savingTheme}>
                {savingTheme ? 'Saving...' : 'Save Theme'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showQR && (
        <div className="qr-modal-overlay" onClick={() => setShowQR(null)}>
          <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="qr-modal-title">QR Code</div>
            <div className="qr-modal-sub">{showQR.title}</div>
            <div className="qr-code-wrap">
              <QRCodeSVG value={showQR.url} size={180} fgColor="#1C1208" bgColor="#FFFFFF" />
            </div>
            <div className="qr-url">{showQR.url}</div>
            <button onClick={() => setShowQR(null)} className="qr-close-btn">Close</button>
          </div>
        </div>
      )}

      {/* ── UPGRADE MODAL ── */}
      {showUpgradeModal && (
        <div className="qr-modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="qr-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="qr-modal-title">🚀 Upgrade to PRO</div>
            <div className="qr-modal-sub" style={{ marginBottom: 24 }}>Unlock location & device analytics</div>

            {/* Plan toggle */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
              {(['monthly', 'yearly'] as const).map(plan => (
                <button key={plan} onClick={() => setUpgradePlan(plan)}
                  style={{ flex: 1, padding: '14px', borderRadius: 12,
                    border: upgradePlan === plan ? '2px solid var(--accent)' : '1.5px solid var(--border)',
                    background: upgradePlan === plan ? 'var(--accent-soft)' : 'var(--surface)',
                    cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  <div style={{ fontWeight: 700, color: 'var(--ink)', textTransform: 'uppercase', marginBottom: 4 }}>{plan}</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--accent)' }}>
                    {plan === 'monthly' ? pricing.monthlyDisplay : pricing.yearlyDisplay}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>
                    {plan === 'yearly' ? `${pricing.yearlySaving} · ${pricing.yearlyPerMonth}` : 'per month'}
                  </div>
                </button>
              ))}
            </div>

            {/* Features */}
            <div style={{ background: 'var(--bg-alt)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
              {['📍 Location analytics (country & city)', '📱 Device & browser breakdown', '💻 OS tracking', '🌐 30-day data history'].map(f => (
                <div key={f} style={{ fontSize: 13, color: 'var(--ink)', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#22c55e', fontWeight: 700 }}>✓</span> {f}
                </div>
              ))}
            </div>

            <button onClick={handleUpgrade} disabled={upgradeLoading}
              style={{ width: '100%', background: 'var(--accent)', color: '#fff', border: 'none',
                borderRadius: 12, padding: '14px', fontFamily: 'var(--font-mono)', fontSize: 13,
                fontWeight: 600, cursor: upgradeLoading ? 'not-allowed' : 'pointer',
                opacity: upgradeLoading ? 0.7 : 1, marginBottom: 10 }}>
              {upgradeLoading ? 'Processing...' : `Upgrade — ${upgradePlan === 'yearly' ? pricing.yearlyDisplay + '/year' : pricing.monthlyDisplay + '/month'} →`}
            </button>
            <button onClick={() => setShowUpgradeModal(false)} className="qr-close-btn">Cancel</button>
          </div>
        </div>
      )}

      {showEditProfile && (
        <EditProfile
          onClose={() => setShowEditProfile(false)}
          currentName={user?.name || ''}
          currentBio={user?.bio || ''}
          currentSocialLinks={user?.socialLinks}
          currentBadge={(user as any)?.badge || ''}
          currentSensitiveContent={(user as any)?.sensitiveContent || false}
        />
      )}

      {/* ── PASSWORD MODAL ── */}
      {showPasswordModal && (
        <div className="qr-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="qr-modal" style={{ maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <div className="qr-modal-title">🔑 {user?.passwordSet ? 'Change Password' : 'Set Password'}</div>
            <div className="qr-modal-sub" style={{ marginBottom: 20 }}>
              {user?.passwordSet
                ? 'Enter your current password to set a new one'
                : 'Set a password for your account'}
            </div>

            {/* Current password — sirf tab dikhao jab already set hai */}
            {user?.passwordSet && (
              <div style={{ marginBottom: 12 }}>
                <label className="modal-label">Current Password</label>
                <input type="password" value={pwdCurrent}
                  onChange={(e) => setPwdCurrent(e.target.value)}
                  placeholder="••••••••"
                  style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg)', color: 'var(--ink)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '11px 14px', fontFamily: 'var(--font-body)', fontSize: 14, outline: 'none' }}
                />
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <label className="modal-label">New Password</label>
              <input type="password" value={pwdNew}
                onChange={(e) => setPwdNew(e.target.value)}
                placeholder="••••••••"
                style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg)', color: 'var(--ink)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '11px 14px', fontFamily: 'var(--font-body)', fontSize: 14, outline: 'none' }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label className="modal-label">Confirm New Password</label>
              <input type="password" value={pwdConfirm}
                onChange={(e) => setPwdConfirm(e.target.value)}
                placeholder="••••••••"
                onKeyDown={(e) => { if (e.key === 'Enter') handleChangePassword(); }}
                style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg)', color: 'var(--ink)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '11px 14px', fontFamily: 'var(--font-body)', fontSize: 14, outline: 'none' }}
              />
            </div>

            <button
              onClick={handleChangePassword}
              disabled={pwdLoading}
              style={{ width: '100%', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontFamily: 'var(--font-mono)', fontSize: 13, cursor: pwdLoading ? 'not-allowed' : 'pointer', opacity: pwdLoading ? 0.6 : 1, marginBottom: 10 }}
            >
              {pwdLoading ? 'Saving...' : user?.passwordSet ? 'Update Password →' : 'Set Password →'}
            </button>
            <button onClick={() => { setShowPasswordModal(false); setPwdCurrent(''); setPwdNew(''); setPwdConfirm(''); }} className="qr-close-btn">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;